import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import DetailPageTemplate from '../components/templates/DetailPageTemplate';
import HeroSection from '../components/organisms/HeroSection';
import SectionHeader from '../components/molecules/SectionHeader';
import FeatureCard from '../components/molecules/FeatureCard';
import ServiceCard from '../components/molecules/ServiceCard';
import Button from '../components/atoms/Button';
import Text from '../components/atoms/Text';
import Badge from '../components/atoms/Badge';

const NationwideTransportDetail = () => {
  const { t } = useTranslation();
  
  // 翻訳されたデータを使用
  const features = t('serviceDetail.nationwide.features.list', { returnObjects: true });
  const vehicleTypes = t('serviceDetail.nationwide.vehicles.types', { returnObjects: true });
  const serviceAreas = t('serviceDetail.nationwide.areas.regions', { returnObjects: true });
  const corporateServices = t('serviceDetail.nationwide.corporate.services', { returnObjects: true });
  const safetyItems = t('serviceDetail.nationwide.safety.items', { returnObjects: true });
  const includedItems = t('serviceDetail.nationwide.pricing.includedItems', { returnObjects: true });
  const optionItems = t('serviceDetail.nationwide.pricing.optionItems', { returnObjects: true });
  const faqItems = t('serviceDetail.nationwide.faq.items', { returnObjects: true });

  return (
    <DetailPageTemplate
      serviceName={t('serviceDetail.nationwide.title')}
      onContactSubmit={(data) => {
        console.log('Nationwide Transport inquiry:', data);
        alert(t('serviceDetail.nationwide.inquirySuccess'));
      }}
    >
      {({ onContactClick }) => (
        <>
          {/* Hero Section */}
          <HeroSection
            badge={t('serviceDetail.nationwide.badge')}
            title={t('serviceDetail.nationwide.title')}
            subtitle={t('serviceDetail.nationwide.subtitle')}
            description={t('serviceDetail.nationwide.description')}
            primaryAction={{
              text: t('serviceDetail.nationwide.primaryAction'),
              onClick: onContactClick
            }}
            secondaryAction={{
              text: t('serviceDetail.nationwide.secondaryAction'),
              onClick: () => document.getElementById('vehicles')?.scrollIntoView({ behavior: 'smooth' })
            }}
          />

          {/* Service Features */}
          <section className="py-20 bg-white">
            <div className="max-w-7xl mx-auto px-4">
              <SectionHeader
                badge={t('serviceDetail.nationwide.features.badge')}
                title={t('serviceDetail.nationwide.features.title')}
                subtitle={t('serviceDetail.nationwide.features.subtitle')}
                className="mb-16"
              />

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
                {features.map((feature, index) => (
                  <FeatureCard
                    key={index}
                    icon={feature.icon}
                    title={feature.title}
                    description={feature.description}
                  />
                ))}
              </div>
            </div>
          </section>

          {/* Vehicle Types */}
          <section id="vehicles" className="py-20 bg-gray-50">
            <div className="max-w-7xl mx-auto px-4">
              <SectionHeader
                badge={t('serviceDetail.nationwide.vehicles.badge')}
                title={t('serviceDetail.nationwide.vehicles.title')}
                subtitle={t('serviceDetail.nationwide.vehicles.subtitle')}
                className="mb-16"
              />

              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                {vehicleTypes.map((vehicle, index) => (
                  <ServiceCard
                    key={index}
                    title={vehicle.title}
                    subtitle={vehicle.capacity}
                    description={vehicle.description}
                    price={vehicle.price}
                    features={vehicle.features}
                    image={t('serviceDetail.nationwide.vehicleImage')}
                    onInquire={onContactClick}
                  />
                ))}
              </div>
            </div>
          </section>

          {/* Service Areas */}
          <section className="py-20 bg-white">
            <div className="max-w-7xl mx-auto px-4">
              <SectionHeader
                badge={t('serviceDetail.nationwide.areas.badge')}
                title={t('serviceDetail.nationwide.areas.title')}
                subtitle={t('serviceDetail.nationwide.areas.subtitle')}
                className="mb-16"
              />

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {serviceAreas.map((area, index) => (
                  <div key={index} className="bg-white rounded-xl shadow-lg p-6 border-l-4 border-orange-500">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-xl font-bold text-gray-800">{area.region}</h3>
                      <Badge variant="secondary" size="small">
                        {area.responseTime}
                      </Badge>
                    </div>
                    <Text size="sm" className="mb-4">{area.prefectures}</Text>
                    <div className="bg-orange-50 p-3 rounded-lg mb-4">
                      <Text size="sm" weight="medium" color="orange-700" className="mb-2">
                        {t('serviceDetail.nationwide.areas.popularTours')}
                      </Text>
                      <ul className="space-y-1">
                        {area.specialties.map((specialty, idx) => (
                          <li key={idx} className="text-sm text-orange-600">
                            • {specialty}
                          </li>
                        ))}
                      </ul>
                    </div>
                    <Button
                      size="small"
                      onClick={onContactClick}
                      className="w-full"
                    >
                      {t('serviceDetail.nationwide.areas.getQuoteForRegion')}
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Corporate Services */}
          <section className="py-20 bg-gray-50">
            <div className="max-w-7xl mx-auto px-4">
              <SectionHeader
                badge={t('serviceDetail.nationwide.corporate.badge')}
                title={t('serviceDetail.nationwide.corporate.title')}
                subtitle={t('serviceDetail.nationwide.corporate.subtitle')}
                className="mb-16"
              />

              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                {corporateServices.map((service, index) => (
                  <div key={index} className="bg-white rounded-xl shadow-lg p-8">
                    <div className="flex items-center mb-4">
                      <div className="text-3xl mr-4">{service.icon}</div>
                      <h3 className="text-xl font-bold text-gray-800">{service.title}</h3>
                    </div>
                    <Text className="mb-6">{service.description}</Text>
                    <ul className="space-y-2 mb-6">
                      {service.features.map((feature, idx) => (
                        <li key={idx} className="flex items-center text-sm text-gray-600">
                          <div className="w-2 h-2 bg-orange-500 rounded-full mr-3"></div>
                          {feature}
                        </li>
                      ))}
                    </ul>
                    <Button
                      size="small"
                      onClick={onContactClick}
                      className="w-full"
                    >
                      {t('serviceDetail.nationwide.corporate.inquireDetails')}
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Safety & Quality */}
          <section className="py-20 bg-white">
            <div className="max-w-7xl mx-auto px-4">
              <SectionHeader
                badge={t('serviceDetail.nationwide.safety.badge')}
                title={t('serviceDetail.nationwide.safety.title')}
                subtitle={t('serviceDetail.nationwide.safety.subtitle')}
                className="mb-16"
              />

              <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {safetyItems.map((item, index) => (
                  <FeatureCard
                    key={index}
                    icon={item.icon}
                    title={item.title}
                    description={item.description}
                  />
                ))}
              </div>
            </div>
          </section>

          {/* Pricing Guide */}
          <section className="py-20 bg-gray-50">
            <div className="max-w-7xl mx-auto px-4">
              <SectionHeader
                badge={t('serviceDetail.nationwide.pricing.badge')}
                title={t('serviceDetail.nationwide.pricing.title')}
                subtitle={t('serviceDetail.nationwide.pricing.subtitle')}
                className="mb-16"
              />

              <div className="bg-white rounded-2xl shadow-xl p-8">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  <div>
                    <h3 className="text-xl font-bold text-gray-800 mb-4">{t('serviceDetail.nationwide.pricing.includedTitle')}</h3>
                    <ul className="space-y-2">
                      {includedItems.map((item, index) => (
                        <li key={index} className="flex items-center text-sm text-gray-600">
                          <div className="w-2 h-2 bg-green-500 rounded-full mr-3"></div>
                          {item}
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-800 mb-4">{t('serviceDetail.nationwide.pricing.optionsTitle')}</h3>
                    <ul className="space-y-2">
                      {optionItems.map((item, index) => (
                        <li key={index} className="flex items-center text-sm text-gray-600">
                          <div className="w-2 h-2 bg-orange-500 rounded-full mr-3"></div>
                          {item}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
                <div className="mt-8 text-center">
                  <Button size="large" onClick={onContactClick}>
                    {t('serviceDetail.nationwide.pricing.requestFreeQuote')}
                  </Button>
                </div>
              </div>
            </div>
          </section>

          {/* FAQ Section */}
          <section className="py-20 bg-white">
            <div className="max-w-7xl mx-auto px-4">
              <SectionHeader
                badge={t('serviceDetail.nationwide.faq.badge')}
                title={t('serviceDetail.nationwide.faq.title')}
                subtitle={t('serviceDetail.nationwide.faq.subtitle')}
                className="mb-16"
              />

              <div className="space-y-6">
                {faqItems.map((faq, index) => (
                  <div key={index} className="bg-gray-50 rounded-lg p-6">
                    <h3 className="text-lg font-bold text-gray-800 mb-2">Q. {faq.question}</h3>
                    <Text>{faq.answer}</Text>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* CTA Section */}
          <section className="py-20 bg-blue-500 text-white">
            <div className="max-w-7xl mx-auto px-4 text-center">
              <h2 className="text-4xl font-bold mb-8">
                {t('serviceDetail.nationwide.cta.title')}
              </h2>
              <Text size="xl" color="white" className="opacity-90 max-w-3xl mx-auto mb-12">
                {t('serviceDetail.nationwide.cta.description')}
              </Text>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button
                  variant="secondary"
                  size="large"
                  onClick={onContactClick}
                  className="bg-white text-blue-500 hover:bg-gray-100"
                >
                  {t('serviceDetail.nationwide.cta.primaryAction')}
                </Button>
                <Link
                  to="/"
                  className="border-2 border-white text-white px-8 py-4 rounded-lg text-lg font-medium hover:bg-white hover:text-blue-500 transition-all duration-200 inline-flex items-center justify-center"
                >
                  {t('serviceDetail.nationwide.cta.secondaryAction')}
                </Link>
              </div>
            </div>
          </section>
        </>
      )}
    </DetailPageTemplate>
  );
};

export default NationwideTransportDetail;
